import React from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import ApplicantsTable from './ApplicantsTable';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { Pagination } from 'ats/components/pagination/Pagination';
import DrawerContainer from 'ats/components/drawer/DrawerContainer';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import FilterBar from 'ats/components/filter-bar/FilterBar';
import FilterDrawer from 'ats/components/filter-drawer/FilterDrawer';
import { buildAppliedFilterItems } from 'ats/components/filter-drawer/buildAppliedFilterItems';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import { ListStore } from 'shared/stores/ListStore';
import ApplicantProfile from './ApplicantProfile';
import { candidateSeniorityOptions } from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { getHireabilityFilterOptions } from 'ats/utils/hireabilityDropdownOptions';
import { ApplicantCreate } from './components/ApplicantCreate';
import {
	createImportListener,
	extractNonceFromUrl,
	isLinkedInImportSource,
	mapLinkedInProfileToApplicant
} from 'ats/utils/importBridge';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { modalStore } from 'components/layout/modals/ModalStore';
import { eventManager } from 'ats/utils/EventManager';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import { PermissionGate } from 'shared/security/PermissionGate';
import { PERMISSIONS, hasPermission } from 'shared/security/rolePermissions';
import moment from 'moment-timezone';
import {
	APPLICANT_STAGE_FILTER_GROUPS,
	getApplicantValueColor,
	renderApplicantStage,
	renderApplicantValueIcon
} from 'ats/components/stage-filter-dropdown/stageFilterPresets';
import ViewsDropdown from 'ats/components/views-dropdown/ViewsDropdown';
import { ListViewController } from 'ats/components/views-dropdown/ListViewController';
import { LIST_VIEW_SECTIONS } from 'ats/components/views-dropdown/listViewSections';
import { attachListProfileDrawerHandlers } from 'ats/utils/openEntityProfile';

@withRouter
@observer
export class Applicants extends React.Component {
	state = {
		activeApplicantId: null,
		searchFilter: null,
		countryFilter: null,
		englishLevelFilter: [],
		ownerFilter: null,
		stageFilter: null,
		seniorityFilter: [],
		cityFilter: null,
		tagFilter: null,
		expectedCompensationFilter: null,
		currentCompensationFilter: null,
		jobRoleFilter: [],
		skillFilter: [],
		importedLinkedInData: null,
		mineOnly: false,
		createdAtFilter: null,
		updatedAtFilter: null,
		lastEngagementAtFilter: null,
		sourceFilter: null,
		referrerFilter: null,
		hireabilityFilter: null,
		publicProfileStatusFilter: [],
		visibleColumns: applicantStore.defaultTableColumnsOptions
	};

	constructor(props) {
		super(props);
		this.listStore = new ListStore(applicantStore, {
			entityName: 'Applicant',
			createModalTitle: 'New Candidate',
			createModalPosition: 'center',
			createModalSize: '2xl',
			createModal: <ApplicantCreate handleSuccess={this.handleCreateSuccess} />
		});
		this.importListenerCleanup = null;
		this.listViewController = new ListViewController(
			LIST_VIEW_SECTIONS.CANDIDATES,
			this
		);
		this.listStore.beginLoading();
	}

	async componentDidMount() {
		await this.listViewController.initialize();

		const applicantIdFromUrl = this.props.match?.params?.id;
		if (applicantIdFromUrl) {
			this.openProfileById(applicantIdFromUrl);
		}

		// Check if page was opened from LinkedIn importer extension
		const isFromExtension = isLinkedInImportSource();
		if (isFromExtension) {
			this.setupLinkedInImportListener();
		}

		eventManager.on('taskChanged', this.handleTaskChanged);
	}

	setupLinkedInImportListener() {
		const nonce = extractNonceFromUrl();

		this.importListenerCleanup = createImportListener({
			expectedNonce: nonce,
			onImport: profile => {
				const mappedData = mapLinkedInProfileToApplicant(profile);

				this.setState(
					{
						importedLinkedInData: mappedData
					},
					() => {
						modalStore.openModal(
							{
								title: 'New Candidate',
								position: 'center',
								size: '2xl'
							},
							<ApplicantCreate
								handleSuccess={this.handleCreateSuccess}
								importedData={this.state.importedLinkedInData}
							/>
						);
					}
				);
			},
			onError: error => {
				notificationStore.pushErrorNotification('Import Error', error);
			}
		});
	}

	componentWillUnmount() {
		this.listViewController.cancel();
		eventManager.off('taskChanged', this.handleTaskChanged);

		this.listStore.store.resetQuery();

		if (drawerStore.isOpen) {
			drawerStore.closeDrawer();
		}

		// Clean up import listener
		if (this.importListenerCleanup) {
			this.importListenerCleanup();
		}
	}

	componentDidUpdate(prevProps) {
		const prevId = prevProps.match?.params?.id;
		const nextId = this.props.match?.params?.id;

		if (prevId === nextId) return;

		if (nextId) {
			this.openProfileById(nextId);
		} else {
			if (
				drawerStore.isOpen &&
				drawerStore.drawerId === drawerStore.DRAWER_IDS.APPLICANT_PROFILE
			) {
				drawerStore.closeDrawer();
			}
			this.setState({ activeApplicantId: null });
		}
	}

	handleCreateSuccess = result => {
		this.listStore.search();
		if (result?.data?.id) {
			this.props.history.push(`${this.getBaseUrl()}/${result.data.id}`);
		}
	};

	handleTaskChanged = () => {
		this.listStore.search();
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	getBaseUrl = () => {
		const url = this.props.match?.url || '';
		const id = this.props.match?.params?.id;
		if (!id) return url;
		return url.replace(new RegExp(`/${id}$`), '');
	};

	openProfileById = applicantId => {
		this.setState({ activeApplicantId: applicantId });

		const onClose = () => {
			this.setState({ activeApplicantId: null });
			drawerStore.closeDrawer();
			this.props.history.push(this.getBaseUrl());
		};
		const onSuccess = () => this.listStore.search();

		if (
			attachListProfileDrawerHandlers({
				drawerId: drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				idKey: 'applicantId',
				entityId: applicantId,
				onClose,
				onSuccess
			})
		) {
			return;
		}

		drawerStore.openDrawer(
			{
				containerId: 'applicants',
				drawerId: drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				showHeader: false,
				props: {
					applicantId,
					onSuccess,
					onClose
				}
			},
			ApplicantProfile
		);
	};

	openProfile = rowData => {
		const baseUrl = this.getBaseUrl();
		this.props.history.push(`${baseUrl}/${rowData.id}`);
		this.openProfileById(rowData.id);
	};

	getBarFilters = () => {
		return [
			{
				type: 'search',
				label: 'Search candidate name, email or LinkedIn URL',
				name: 'search',
				onKeyPress: newValue => {
					this.setState({
						searchFilter: newValue
					});
				},
				onChange: newValue => {
					this.listStore.store.setQuery('text', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.searchFilter
			},
			{
				type: 'stage-filter-dropdown',
				label: 'Internal Stage',
				placeholder: 'Internal Stage',
				name: 'stage',
				onChange: newValue => {
					const stages = newValue || [];
					this.setState({
						stageFilter: stages
					});
					this.listStore.store.setQuery(
						'stage',
						stages.map(stage => stage.value)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.stageFilter,
				options: applicantStore.stageOptions,
				groups: APPLICANT_STAGE_FILTER_GROUPS,
				stageToStatus: applicantStore.stageToStatus,
				renderStage: renderApplicantStage,
				renderValueIcon: renderApplicantValueIcon,
				getValueColor: getApplicantValueColor
			},
			{
				type: 'user-dropdown',
				label: 'Owner',
				name: 'owner',
				placeholder: 'Owner: All',
				role: NON_CANDIDATE_USER_ROLES,
				isClearable: true,
				onChange: newValue => {
					this.setState({
						ownerFilter: newValue
					});
					this.listStore.store.setQuery('ownerId', newValue?.id);
					this.handlePaginationChange(1);
				},
				value: this.state.ownerFilter
			}
		];
	};

	getDrawerFilters = () => {
		return [
			{
				type: 'job-role-dropdown',
				label: 'Potential Roles',
				name: 'job-roles',
				onChange: newValue => {
					this.setState(
						{
							jobRoleFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'jobRoleIds',
						newValue?.map(v => v.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.jobRoleFilter
			},
			{
				type: 'multi-dropdown',
				label: 'English Level',
				name: 'english-level',
				onChange: newValue => {
					this.setState(
						{
							englishLevelFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'englishLevel',
						newValue?.map(v => v.value)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.englishLevelFilter,
				options: [
					...candidatesStore.englishLevelOptions.map(englishLevel => ({
						...englishLevel,
						label: englishLevel.text
					}))
				],
				getOptionLabel: option => option.label
			},
			{
				type: 'multi-dropdown',
				label: 'Seniority',
				name: 'seniority',
				onChange: newValue => {
					this.setState(
						{
							seniorityFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'seniority',
						newValue?.map(v => v.value)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.seniorityFilter,
				options: [
					...candidateSeniorityOptions.map(seniority => ({
						...seniority,
						label: seniority.text
					}))
				],
				getOptionLabel: option => option.label
			},
			{
				type: 'country-dropdown',
				label: 'Country',
				name: 'country',
				isClearable: true,
				isMulti: true,
				onChange: newValue => {
					this.setState(
						{
							countryFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'countryId',
						newValue?.map(v => v.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.countryFilter
			},
			{
				type: 'skill-dropdown',
				label: 'Technologies',
				name: 'skills',
				onChange: newValue => {
					this.setState(
						{
							skillFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'skillIds',
						newValue?.map(v => v.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.skillFilter
			},
			{
				type: 'text',
				label: 'City',
				name: 'city',
				onKeyPress: newValue => {
					this.setState(
						{
							cityFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
				},
				onChange: newValue => {
					this.listStore.store.setQuery('city', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.cityFilter
			},
			{
				type: 'tags-dropdown',
				label: 'Tags',
				isClearable: true,
				onChange: newValue => {
					this.setState(
						{
							tagFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery(
						'tags',
						newValue.map(t => t.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.tagFilter
			},
			{
				type: 'calendar',
				label: 'Created At',
				placeholder: 'MM/DD/YYYY',
				name: 'created-at',
				isRange: true,
				onChange: dates => {
					this.setState({ createdAtFilter: dates }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});

					if (dates?.[0] && dates?.[1]) {
						this.listStore.store.setQuery('createdAt', [
							moment(dates[0]).startOf('day').toDate(),
							moment(dates[1]).endOf('day').toDate()
						]);
					} else {
						this.listStore.store.setQuery('createdAt', null);
					}
					this.handlePaginationChange(1);
				},
				value: this.state.createdAtFilter
			},
			{
				type: 'calendar',
				label: 'Updated At',
				placeholder: 'MM/DD/YYYY',
				name: 'updated-at',
				isRange: true,
				onChange: dates => {
					this.setState({ updatedAtFilter: dates }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});

					if (dates?.[0] && dates?.[1]) {
						this.listStore.store.setQuery('updatedAt', [
							moment(dates[0]).startOf('day').toDate(),
							moment(dates[1]).endOf('day').toDate()
						]);
					} else {
						this.listStore.store.setQuery('updatedAt', null);
					}
					this.handlePaginationChange(1);
				},
				value: this.state.updatedAtFilter
			},
			{
				type: 'calendar',
				label: 'Last Engagement',
				placeholder: 'MM/DD/YYYY',
				name: 'last-engagement-at',
				isRange: true,
				onChange: dates => {
					this.setState({ lastEngagementAtFilter: dates }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});

					if (dates?.[0] && dates?.[1]) {
						this.listStore.store.setQuery('lastEngagementAt', [
							moment(dates[0]).startOf('day').toDate(),
							moment(dates[1]).endOf('day').toDate()
						]);
					} else {
						this.listStore.store.setQuery('lastEngagementAt', null);
					}
					this.handlePaginationChange(1);
				},
				value: this.state.lastEngagementAtFilter
			},
			{
				type: 'user-dropdown',
				label: 'Source',
				name: 'source',
				placeholder: ' ',
				role: NON_CANDIDATE_USER_ROLES,
				isClearable: true,
				onChange: newValue => {
					this.setState({ sourceFilter: newValue }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});
					this.listStore.store.setQuery('sourceId', newValue?.id);
					this.handlePaginationChange(1);
				},
				value: this.state.sourceFilter
			},
			{
				type: 'applicant-dropdown',
				label: 'Referrer',
				name: 'referrer',
				placeholder: ' ',
				isClearable: true,
				onChange: newValue => {
					this.setState({ referrerFilter: newValue }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});
					this.listStore.store.setQuery('referrerId', newValue?.id ?? null);
					this.handlePaginationChange(1);
				},
				value: this.state.referrerFilter
			},
			{
				type: 'dropdown',
				label: 'Hireability',
				name: 'hireability',
				placeholder: ' ',
				optionValue: 'value',
				onChange: newValue => {
					const hireability =
						typeof newValue === 'string' ? newValue : (newValue?.value ?? null);
					this.setState({ hireabilityFilter: hireability }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});
					this.listStore.store.setQuery('hireability', hireability);
					this.handlePaginationChange(1);
				},
				value: this.state.hireabilityFilter,
				options: getHireabilityFilterOptions()
			},
			{
				type: 'multi-dropdown',
				label: 'Public Profile',
				name: 'public-profile-status',
				onChange: newValue => {
					this.setState({ publicProfileStatusFilter: newValue }, () => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					});
					this.listStore.store.setQuery(
						'publicProfileStatus',
						newValue?.length ? newValue.map(v => v.value) : null
					);
					this.handlePaginationChange(1);
				},
				value: this.state.publicProfileStatusFilter,
				options: applicantStore.publicProfileStatusOptions,
				getOptionLabel: option => option.label
			},
			{
				type: 'compensation',
				label: 'Current Compensation',
				name: 'current-compensation',
				onChange: newValue => {
					this.setState(
						{
							currentCompensationFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery('currentCompensation', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.currentCompensationFilter
			},
			{
				type: 'compensation',
				label: 'Expected Compensation',
				name: 'expected-compensation',
				onChange: newValue => {
					this.setState(
						{
							expectedCompensationFilter: newValue
						},
						() => {
							drawerStore.updateDrawerProps({
								filters: this.getDrawerFilters()
							});
						}
					);
					this.listStore.store.setQuery('expectedCompensation', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.expectedCompensationFilter
			}
		];
	};

	getAppliedFilters = () =>
		buildAppliedFilterItems([
			...this.getBarFilters(),
			...this.getDrawerFilters()
		]);

	resetDrawerFilters = () => {
		this.setState(
			{
				searchFilter: '',
				countryFilter: null,
				englishLevelFilter: [],
				ownerFilter: null,
				stageFilter: null,
				seniorityFilter: [],
				cityFilter: '',
				tagFilter: null,
				expectedCompensationFilter: null,
				currentCompensationFilter: null,
				jobRoleFilter: [],
				skillFilter: [],
				mineOnly: false,
				createdAtFilter: null,
				updatedAtFilter: null,
				lastEngagementAtFilter: null,
				sourceFilter: null,
				referrerFilter: null,
				hireabilityFilter: null,
				publicProfileStatusFilter: []
			},
			() => {
				drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
			}
		);

		this.listStore.store.resetQuery();
		this.listStore.store.setQuery('sort', { column: 'createdAt', asc: false });
		this.handlePaginationChange(1);
	};

	renderApplicantsEmptyState = () => {
		if (this.state.mineOnly) {
			return (
				<EmptyState
					size="sm"
					icon={EMPTY_STATE_ICONS.allCaughtUp}
					iconAlt="All caught up"
					message="All caught up"
					detail="You’re viewing only your pending tasks. Want to see all data?"
					actionText="Turn off Me mode"
					onActionClick={() => {
						this.setState({ mineOnly: false });
						this.listStore.store.setQuery('mineOnly', null);
						this.handlePaginationChange(1);
					}}
				/>
			);
		}

		const hasFilters = this.getAppliedFilters().length > 0;
		const canCreate = hasPermission(PERMISSIONS.APPLICANT_CREATE);

		return (
			<EmptyState
				icon={
					hasFilters
						? EMPTY_STATE_ICONS.noResultsCandidates
						: EMPTY_STATE_ICONS.addCandidate
				}
				iconAlt={hasFilters ? 'No matching candidates' : 'No candidates yet'}
				message={
					hasFilters
						? 'No candidates match your search criteria.'
						: 'No candidates yet.'
				}
				detail={
					hasFilters
						? 'Try adjusting your filters or search terms to see more results.'
						: 'Create a candidate to get started.'
				}
				actionText={
					hasFilters ? 'Clear filters' : canCreate ? 'New Candidate' : undefined
				}
				onActionClick={
					hasFilters
						? this.resetDrawerFilters
						: canCreate
							? () => this.listStore.handleCreate()
							: undefined
				}
			/>
		);
	};

	render() {
		const isProfileOpen =
			drawerStore.isOpen &&
			[
				drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				drawerStore.DRAWER_IDS.CLIENT_PROFILE
			].includes(drawerStore.drawerId);

		const isFilterDrawerOpen =
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.FILTER_DRAWER;

		return (
			<div className="Applicants">
				<div className="header-container">
					<h2>Candidates</h2>
					<ViewsDropdown
						section={LIST_VIEW_SECTIONS.CANDIDATES}
						controller={this.listViewController}
						viewStateKey={this.listViewController.getViewStateKey()}
						isModified={this.listViewController.isModified()}
					/>
					<FilterBar
						filters={this.getBarFilters()}
						appliedFilters={this.getAppliedFilters()}
						showMineToggle
						mineOn={this.state.mineOnly}
						onMineChange={nextValue => {
							this.setState({ mineOnly: nextValue });
							this.listStore.store.setQuery(
								'mineOnly',
								nextValue ? true : null
							);
							this.handlePaginationChange(1);
						}}
						onShowMore={() => {
							if (!isFilterDrawerOpen) {
								drawerStore.openDrawer(
									{
										containerId: 'applicants',
										drawerId: drawerStore.DRAWER_IDS.FILTER_DRAWER,
										customHeader: (
											<>
												<button
													className="filter-drawer-back-btn"
													onClick={() => drawerStore.closeDrawer()}>
													<img
														src="/ats/icons/arrow-right-purple-icon.svg"
														alt=""
													/>
												</button>
												<h3>Filters</h3>
											</>
										),
										props: {
											filters: this.getDrawerFilters(),
											getAppliedFilters: this.getAppliedFilters,
											onReset: this.resetDrawerFilters
										}
									},
									FilterDrawer
								);
							} else {
								drawerStore.closeDrawer();
							}
						}}
					/>
					<PermissionGate permission={PERMISSIONS.APPLICANT_CREATE}>
						<button
							type="button"
							className="ats-button-primary"
							onClick={() => this.listStore.handleCreate()}>
							New Candidate
						</button>
					</PermissionGate>
				</div>

				<main>
					<div
						className={`applicants-wrapper${
							isProfileOpen ? ' is-profile-open' : ''
						}`}>
						<div className="applicants-container">
							<div className="ats-table-wrapper">
								<ApplicantsTable
									data={this.listStore.data}
									sort={this.listStore.store.query.sort}
									onSort={this.listStore.handleSort}
									onSuccess={() => this.listStore.search()}
									onClickRow={this.openProfile}
									activeRowId={this.state.activeApplicantId}
									visibleColumns={this.state.visibleColumns}
									onVisibleColumnsChange={visibleColumns =>
										this.setState({ visibleColumns })
									}
									loading={this.listStore.loading}
									isEmpty={
										!this.listStore.loading && this.listStore.data.length === 0
									}
									emptyState={this.renderApplicantsEmptyState()}
								/>
							</div>
						</div>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							totalResults={this.listStore.store.query.pagination.totalResults}
							onPaginationChange={this.handlePaginationChange}
						/>
					</div>
					<DrawerContainer id="applicants" />
				</main>
			</div>
		);
	}
}
